import React, { useEffect, useState } from 'react';
/**
 * `RenewalsDirectDebit` captures direct debit details for the renewals journey.
 *
 * Behaviour:
 * - Renders the shared `DirectDebit` component with renewal-specific flags.
 * - Coordinates validation/update cycles and enables/disables the confirm
 *   button based on `canContinue`.
 * - Branches forward flow: subscription selections go to single payment,
 *   otherwise triggers policy registration via `onRegister`.
 * - For call-centre journeys, overrides the direct debit agreement copy.
 * - Navigates to confirmation once `orderNumbers` are populated.
 */

import { useNavigate } from 'react-router-dom';
import {
  Journey,
  PageAbsolutePath,
  RegistrationState,
  ProductChannel,
  PageRelativePath
} from '../../../consts/Common';
import DirectDebit from '../../common/DirectDebit/DirectDebit';
import { DirectDebitDetails } from '../../common/CommonTypes';
import { hasPaymentSelected } from '../../common/ProductMethods';
import { PaymentMethod } from '../../common/Constants';
import PageHeader from '../../common/PageHeader';
import { ButtonContinue } from '../../common/Button/ButtonContinue';
import { ButtonBack } from '../../common/Button/ButtonBack';
import { useJourney } from '../Layout';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../app-state/Hooks';

const RenewalsDirectDebit = (): JSX.Element => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { journey, onPageChange, setDirectDebitDetails, onRegister } =
    useJourney();

  const {
    directDebitDetails,
    products,
    orderNumbers,
    registrationState,
    campaignId
  } = journey;

  const [goBack, setGoBack] = useState(false);
  const [canContinue, setCanContinue] = useState(false);
  const [validationInProgress, setValidationInProgress] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const [updateDirectDebitDetails, setUpdateDirectDebitDetails] =
    useState(false);
  const [updateComplete, setUpdateComplete] = useState(false);
  const [goForward, setGoForward] = useState(false);
  const [redirect, setRedirect] = useState('');
  const [submissionInProgress, setSubmissionInProgress] = useState(false);

  const userToken = useAppSelector((s) => s.user.token);
  const directDebitPage = useAppSelector((s) => s.content.directDebitPage);
  const appConfig = useAppSelector((s) => s.appConfig);

  const token = journey.token || userToken;
  const cms = directDebitPage;
  const isCallCentreJourney = appConfig.journeyType === Journey.CallCentre;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const onValidated = (valid: boolean) => {
    if (!valid) {
      setValidationInProgress(false);
    }
    setIsValid(valid);
  };

  useEffect(() => {
    if (redirect !== '') {
      navigate(redirect);
    }
  }, [redirect]);

  useEffect(() => {
    if (orderNumbers && orderNumbers.length > 0) {
      setRedirect(PageRelativePath.Confirmation);
    }
  }, [orderNumbers]);

  useEffect(() => {
    if (goForward === true && updateComplete === true) {
      if (hasSubscription === true) {
        setRedirect(PageRelativePath.SinglePayment);
      } else {
        onRegister('', '', campaignId);
      }
    }
  }, [updateComplete]);

  const onUpdateDirectDebitDetails = (
    canContinueToNextPage: boolean,
    directDebit: DirectDebitDetails
  ) => {
    setCanContinue(canContinueToNextPage);
    setDirectDebitDetails(directDebit);
    if (updateDirectDebitDetails === true) {
      setUpdateComplete(true);
      setUpdateDirectDebitDetails(false);
    }
  };

  useEffect(() => {
    if (goBack === true) {
      setRedirect(PageRelativePath.Summary);
    } else if (goForward === true) {
      if (hasSubscription !== true) {
        onPageChange(PageAbsolutePath.Renewals.DirectDebit, true);
      }
    }
  }, [goForward, goBack]);

  useEffect(() => {
    if (isValid === true) {
      setUpdateDirectDebitDetails(true);
      setUpdateComplete(false);
    }
  }, [isValid]);

  useEffect(() => {
    if (registrationState === RegistrationState.Registering) {
      setSubmissionInProgress(true);
    } else {
      setSubmissionInProgress(false);
    }
  }, [registrationState]);

  const hasSubscription = hasPaymentSelected(
    products,
    PaymentMethod.Subscription
  );

  const getLoadingMessage = () => {
    return hasSubscription === true
      ? t('customer-single-payment.processing')
      : t('customer-single-payment.register-policy');
  };

  const onBack = () => {
    setGoBack(true);
    onPageChange(PageAbsolutePath.Renewals.DirectDebit, isValid);
  };

  const onContinue = () => {
    setGoForward(true);
    setValidationInProgress(true);
  };

  return (
    <div>
      {!validationInProgress && !submissionInProgress && (
        <PageHeader
          title={cms.title}
          description={cms.description}
          information={cms.information}
          warning={cms.warning}
          descriptionClass='p2'
        />
      )}

      <DirectDebit
        initialDirectDebitDetails={directDebitDetails}
        products={products}
        update={updateDirectDebitDetails}
        validate={validationInProgress}
        token={token}
        onValidated={onValidated}
        onUpdateDirectDebitDetails={onUpdateDirectDebitDetails}
        showLoading={submissionInProgress}
        loadingMessage={getLoadingMessage()}
        displayCustomerFriendlyName={true}
        productChannel={ProductChannel.RenewalJourney}
        agreeDirectDebitTerms={
          isCallCentreJourney
            ? 'The customer confirms they are fully authorised to set up a Direct Debit in this account.'
            : undefined
        }
        agreeDirectDebitDueDateTerms={
          isCallCentreJourney
            ? 'The customer agrees to pay the instalment amount by the due date. Customer understands the contract term and that cover will automatically cease if fail to pay the instalment amount by the required due date.'
            : undefined
        }
        displaySendDDGinWritingTerms={isCallCentreJourney}
        directDebitPayee={cms.guaranteePayee}
        culture={appConfig.cultureCode}
      />
      {!validationInProgress && !submissionInProgress && (
        <>
          <div className='flex-row form-nav'>
            <ButtonBack
              name='btnBack'
              onClick={onBack}
              text={t('customer-button.back-buttont-text')}
            />
            <ButtonContinue
              name='btnCheckout'
              text={t('customer-button.button-confirm-payment')}
              onClick={onContinue}
              disabled={!canContinue}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default RenewalsDirectDebit;
